import { useEffect } from 'react';

// Lock the page behind an open overlay. position:fixed (not just
// overflow:hidden) because iOS Safari/WKWebView still scrolls the document
// on touch with overflow alone; the scroll position is stashed in `top` and
// restored on unlock so the page doesn't jump back to the start.
//
// Locks are reference-counted: nested overlays (a modal opening a modal)
// each hold the lock, and the page only unlocks when the last one closes.
// Without the count, closing an inner sheet would release the outer one's
// lock and let the page scroll behind it.
let lockCount = 0;
let saved = null;

const lock = () => {
  lockCount += 1;
  if (lockCount > 1) return;
  const scrollY = window.scrollY;
  saved = {
    overflow: document.body.style.overflow,
    position: document.body.style.position,
    width: document.body.style.width,
    top: document.body.style.top,
    rootOverflow: document.documentElement.style.overflow,
    scrollY,
  };
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  document.body.style.top = `-${scrollY}px`;
  // Some overlays (BottomSheet, celebrations) also need the root scrollbar
  // gone; harmless for everyone else since the body is frozen anyway.
  document.documentElement.style.overflow = 'hidden';
};

const unlock = () => {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount > 0 || !saved) return;
  document.body.style.overflow = saved.overflow;
  document.body.style.position = saved.position;
  document.body.style.width = saved.width;
  document.body.style.top = saved.top;
  document.documentElement.style.overflow = saved.rootOverflow;
  window.scrollTo(0, saved.scrollY);
  saved = null;
};

/** Lock page scroll while `active` is true. */
export const useScrollLock = (active) => {
  useEffect(() => {
    if (!active) return undefined;
    lock();
    return unlock;
  }, [active]);
};

// Imperative pair for class components (pair every lock with exactly one
// unlock, e.g. componentDidMount/componentWillUnmount). Function components
// should use the hook.
export const lockBodyScroll = lock;
export const unlockBodyScroll = unlock;

export default useScrollLock;
